import {
  LayoutDashboard,
  Columns,
  Radio,
  ListChecks,
  Inbox,
  ReceiptText,
  Users,
  ClipboardList,
  Landmark,
  MailQuestion,
  CalendarClock,
  PanelLeftClose,
  PanelLeftOpen,
} from 'lucide-react';
import { NavLink, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils.js';
import { toast } from '@/components/ui/toaster.jsx';
import { useShellStore } from '@/stores/shell-store.js';

// Primary nav (MV-10 3.1, Ship 4a): Queue leads (the landing surface), then
// Traffic Pattern / Command Center / Accounts / Follow-ups / Trust / Admin,
// exactly the seven the spec rules. Everything else that still has a live
// route folds into the More group below so no surface orphans. Routed
// entries navigate; placeholders toast until their surface ships.
const NAV = [
  { label: 'Queue', Icon: Inbox, to: '/', end: true },
  { label: 'Traffic Pattern', Icon: ListChecks, to: '/traffic' },
  { label: 'Command Center', Icon: Radio, to: '/command-center' },
  { label: 'Accounts', Icon: Users, to: '/search' },
  { label: 'Follow-ups', Icon: MailQuestion, to: '/follow-ups' },
  { label: 'Renewals', Icon: CalendarClock, to: '/renewals' },
  { label: 'Money', Icon: Landmark, to: '/money' },
  { label: 'Admin', Icon: LayoutDashboard, to: '/admin' },
];

const NAV_MORE = [
  { label: 'Pipeline', Icon: Columns, to: '/pipeline' },
  { label: 'Quote inbox', Icon: ReceiptText, to: '/quote-inbox' },
  { label: 'Blank PHF', Icon: ClipboardList, to: '/blank-phf' },
];

function readRecent() {
  try {
    const raw = JSON.parse(localStorage.getItem('tracon:recent-deals'));
    return Array.isArray(raw) ? raw.slice(0, 5) : [];
  } catch {
    return [];
  }
}

const STAGE_DOT = {
  bound: 'bg-success',
  quoting: 'bg-info',
  warn: 'bg-warning',
};

export default function SideNav() {
  const navigate = useNavigate();
  const collapsed = useShellStore((s) => s.navCollapsed);
  const toggleNav = useShellStore((s) => s.toggleNav);
  const recent = readRecent();

  const itemCls = (active) =>
    cn(
      'relative flex items-center gap-2.5 rounded-card px-2 py-1.5 text-[12.5px]',
      collapsed && 'justify-center px-0',
      active
        ? 'bg-surface-strong text-cream'
        : 'text-text-muted hover:bg-surface-strong/60 hover:text-text'
    );

  const renderNavEntry = ({ label, Icon, to, end }) =>
    to ? (
      <NavLink
        key={label}
        to={to}
        end={end}
        title={collapsed ? label : undefined}
        className={({ isActive }) =>
          cn(
            itemCls(isActive),
            'mb-px',
            isActive &&
              !collapsed &&
              'before:absolute before:left-0 before:top-1 before:bottom-1 before:w-0.5 before:rounded-r before:bg-accent before:content-[""]'
          )
        }
      >
        <Icon size={18} strokeWidth={1.5} className="shrink-0" />
        {!collapsed && <span className="flex-1 truncate">{label}</span>}
      </NavLink>
    ) : (
      <button
        key={label}
        type="button"
        title={collapsed ? label : undefined}
        onClick={() => toast(`${label} is coming soon`)}
        className={cn(itemCls(false), 'mb-px w-full')}
      >
        <Icon size={18} strokeWidth={1.5} className="shrink-0" />
        {!collapsed && <span className="flex-1 truncate text-left">{label}</span>}
      </button>
    );

  return (
    <aside
      className={cn(
        'hidden shrink-0 flex-col border-r border-border bg-surface transition-[width] duration-200 md:flex',
        collapsed ? 'w-nav-w-closed' : 'w-nav-w'
      )}
    >
      <nav className="flex-1 overflow-y-auto px-2 py-2.5">
        {NAV.map(renderNavEntry)}

        <div className="mt-3 pt-1">
          {!collapsed && (
            <h4 className="px-1.5 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-text-dim">
              More
            </h4>
          )}
          {NAV_MORE.map(renderNavEntry)}
        </div>

        {recent.length > 0 && (
          <div className="mt-3 pt-1">
            {!collapsed && (
              <h4 className="px-1.5 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-text-dim">
                Recent
              </h4>
            )}
            {recent.map((d) => (
              <button
                key={d.id}
                type="button"
                title={collapsed ? d.name : undefined}
                onClick={() =>
                  navigate(`/account/${encodeURIComponent(d.accountId || d.id)}/deal/${encodeURIComponent(d.id)}`)
                }
                className={cn(
                  'flex w-full items-center gap-2 rounded-card px-2 py-1.5 text-text-muted hover:bg-surface-strong/60 hover:text-text',
                  collapsed && 'justify-center px-0'
                )}
              >
                <span className="grid h-[18px] w-[18px] shrink-0 place-items-center rounded-[3px] bg-surface-strong text-[9px] font-bold tracking-tight text-text-muted">
                  {(d.name || '?').slice(0, 2).toUpperCase()}
                </span>
                {!collapsed && (
                  <>
                    <span className="flex-1 truncate text-left text-xs">{d.name}</span>
                    <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', STAGE_DOT[d.stage] || 'bg-text-dim')} />
                  </>
                )}
              </button>
            ))}
          </div>
        )}
      </nav>

      <button
        type="button"
        onClick={toggleNav}
        className="flex items-center justify-center gap-2 border-t border-border py-2 text-[11px] text-text-dim hover:bg-surface-strong hover:text-text"
        aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
      >
        {collapsed ? (
          <PanelLeftOpen size={14} strokeWidth={1.5} />
        ) : (
          <>
            <PanelLeftClose size={14} strokeWidth={1.5} />
            <span>Collapse</span>
          </>
        )}
      </button>
    </aside>
  );
}
